import { isNotNullish, isNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { Kostenstelle } from '../../groups/entities/Kostenstelle.js';
import type { ContractSupplementApplyHistory } from '../entities/AppliedContract.js';
import { type ContractSupplementChange, ContractSupplementDataType } from '../entities/ContractSupplement.js';
import { getParsedValueOfSupplementChange } from './getParsedValueOfSupplementChange.js';
import { translateContractPath } from './translateContractPath.js';

export const ResolveKostenStellenName = stateful((props: { kostenstelleID: Maybe<string> }) => {
  const { value, loading } = useQuery(
    () =>
      getByID(
        Kostenstelle,
        props.kostenstelleID ?? '',
      )({
        _id: 1,
        name: 1,
      }),
    [props.kostenstelleID],
  );

  if (loading) return '⌛';
  return value?.name ?? 'X';
});

const ChangeArrow = html`<span class="text-base-content/70">→</span>`;

/**
 * Returns a visually pleasing representation of the value. For example it maps objects to html
 * @param change
 * @returns an html template for the changes value
 */
export function getReadableValueOfChange(
  change: NarrowedEntity<ContractSupplementChange, { value: 1; type: 1; path: 1 }>,
  options?: {
    forceString?: boolean;
    history?: ContractSupplementApplyHistory;
  },
) {
  const suppParsedValue = getParsedValueOfSupplementChange(change);

  const historyForChange = options?.history?.paths?.find(pathHistory => pathHistory.path === change.path);
  const oldValueOnChange = historyForChange?.changes?.at(0);
  const parsed = oldValueOnChange ? getParsedValueOfSupplementChange(oldValueOnChange) : undefined;

  if (
    historyForChange?.changes?.length &&
    historyForChange.changes.at(0)?.value === historyForChange.changes.at(1)?.value
  )
    return nothing;

  switch (change.type) {
    case ContractSupplementDataType.DATE:
      return html`
        <div class="flex flex-row items-center gap-1">
          ${
            historyForChange?.changes?.length
              ? html`
                  <span> ${parsed instanceof Date ? formatCalendarDay(parsed, 'dd.MM.yyyy') : 'Kein Eintrag'} </span>
                  ${ChangeArrow}
                `
              : nothing
          }
          <span>${formatCalendarDay(suppParsedValue as Date, 'dd.MM.yyyy')}</span>
        </div>
      `;
    case ContractSupplementDataType.BOOLEAN:
      return html`
        <div class="flex flex-row items-center gap-1">
          ${
            historyForChange?.changes?.length
              ? html`
                  <span> ${isNotNullish(parsed) ? (parsed ? 'Ja' : 'Nein') : 'Kein Eintrag'} </span>
                  ${ChangeArrow}
                `
              : nothing
          }
          <span> ${suppParsedValue ? 'Ja' : 'Nein'} </span>
        </div>
      `;
    case ContractSupplementDataType.JSON: {
      const parseObject = (obj: object, parseOptions?: { path?: string; index?: number }) => {
        const historyObjects: any[] = [];
        if (isNotNullish(parseOptions?.index)) {
          const historyParsed = (historyForChange?.changes?.filter(c => c.value !== '[]') ?? []).map(c =>
            JSON.parse(c.value),
          );

          for (const entry of historyParsed) {
            const innerHistoryEntry = entry[parseOptions.index];
            if (innerHistoryEntry) {
              historyObjects.push(innerHistoryEntry);
            }
          }
        }

        if (historyObjects.length === 1) {
          historyObjects.unshift({ kostenstelleID: 'Kein Eintrag', prozentsatz: 'Kein Eintrag' });
        }

        return Object.keys(obj)
          .filter(v => v !== '_class' && v !== '_id')
          .map(key => {
            const historyForKey = historyObjects.filter(isNotNullish)?.map(historyChange => historyChange[key]);

            // do not render change if it is the same. This happens as the change is not calculate per field and instead on the kostenstellen object
            if (historyForKey && historyForKey?.[0] === obj[key]) {
              return nothing;
            }

            const keyPath = parseOptions?.path?.length ? `${parseOptions?.path}.${key}` : key;

            return typeof obj[key] === 'object' && !isNullish(obj[key])
              ? parseObject(obj[key], {
                  path: keyPath,
                  index: parseOptions?.index,
                })
              : html`
                  <div class="grid gap-2" style="grid-template-columns: 150px 1fr">
                    <span class="font-semibold"> ${translateContractPath(keyPath)}: </span>
                    ${
                      keyPath.includes('kostenstelle') && !keyPath.includes('kostenstelleID') && isNullish(obj[key])
                        ? `Kostenstelle wird später über die API geladen.`
                        : html`
                            <div class="flex flex-row items-center gap-1">
                              ${
                                historyForKey.length
                                  ? repeat(
                                      historyForKey,
                                      v => v._id,
                                      (v, i) => html`
                                        <span>
                                          ${
                                            key === 'kostenstelleID'
                                              ? html`${ResolveKostenStellenName({ kostenstelleID: v })}`
                                              : v
                                          }
                                        </span>
                                        ${i !== historyForKey.length - 1 ? ChangeArrow : nothing}
                                      `,
                                    )
                                  : html`
                                      <span>
                                        ${
                                          key === 'kostenstelleID'
                                            ? html`${ResolveKostenStellenName({ kostenstelleID: obj[key] })}`
                                            : obj[key]
                                        }
                                      </span>
                                    `
                              }
                            </div>
                          `
                    }
                  </div>
                `;
          });
      };

      if (Array.isArray(suppParsedValue))
        return html`
          <div class="flex flex-col gap-2 pb-4">
            ${repeat(
              suppParsedValue,
              v => v._id,
              (v, i) => html`
                ${i !== 0 ? html`<hr class="border-base-300" />` : nothing}
                <div class="flex flex-col gap-1">${parseObject(v, { index: i })}</div>
              `,
            )}
          </div>
        `;
      return html` <div class="flex flex-col gap-1">${parseObject(suppParsedValue)}</div> `;
    }
    case ContractSupplementDataType.NEW_FIELD:
      return options?.forceString
        ? html` <div class="flex flex-row items-center gap-1">
            ${
              historyForChange?.changes?.length
                ? html`
                    <span> ${parsed ?? 'Kein Eintrag'} </span>
                    ${ChangeArrow}
                  `
                : nothing
            }
            <span> Feld neu erstellt </span>
          </div>`
        : isNotNullish(suppParsedValue)
          ? Array.isArray(suppParsedValue) && suppParsedValue.length < 1
            ? nothing
            : html`<span> ${suppParsedValue} </span>`
          : nothing;
    case ContractSupplementDataType.NULL:
      return options?.forceString
        ? html`
            <div class="flex flex-row items-center gap-1">
              ${
                historyForChange?.changes?.length
                  ? html`
                      <span> ${parsed ?? 'Kein Eintrag'} </span>
                      ${ChangeArrow}
                    `
                  : nothing
              }
              <span> Eintrag gelöscht </span>
            </div>
          `
        : nothing;
    default:
      return html`
        <div class="flex flex-row items-center gap-1">
          ${
            historyForChange?.changes?.length
              ? html`
                  <span>
                    ${
                      change.path.includes('kostenstelleID')
                        ? html`${ResolveKostenStellenName({ kostenstelleID: parsed })}`
                        : (parsed ?? 'Kein Eintrag')
                    }
                  </span>
                  ${ChangeArrow}
                `
              : nothing
          }
          <span
            >${
              change.path.includes('kostenstelleID')
                ? html`${ResolveKostenStellenName({ kostenstelleID: suppParsedValue })}`
                : suppParsedValue
            }</span
          >
        </div>
      `;
  }
}
